import { useTranslation } from 'react-i18next'
import { MenuItem, TextField } from '@mui/material'
import { forwardRef, useImperativeHandle, Dispatch, SetStateAction, useContext, useEffect, useState, useRef } from 'react'

import { parseNumber } from './utils'
import { AuthContext } from '../../contexts'
import { BankAccount, Customer } from '../../types'
import { requestAccessToken } from '../../api/auth.api'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { MexicanCustomerPaymentForm } from './mexican-customer-payment-form'
import { BrazilianCustomerPaymentForm } from './brazilian-customer-payment-form'
import { ColombianCustomerPaymentForm } from './colombian-customer-payment-form'
import { getBalanceBySelectedBankingPartner, sendPaymentTest } from '../../api/app-queries.api'
import { bankingPartnersByCountry, countriesCurrencies, countriesNames, customersBankAccountType, customersDocumentType } from '../../lib'
import { AgentinianCustomerPaymentForm } from './argentinian-customer-payment-form'
import { ChileanCustomerPaymentForm } from './chilean-customer-payment-form'

export const BankingPartnersPaymentsTestForm = forwardRef(
  (
    {
      customerData,
      setCustomerData,
      handleIsLoading,
      handleCloseSession,
      handleEnableActionButton
    }: {
      handleCloseSession: () => void
      customerData: Partial<Customer> | null
      handleIsLoading: (loadingState: boolean) => void
      handleEnableActionButton: (enableActionButton: boolean) => void
      setCustomerData: Dispatch<SetStateAction<Partial<Customer> | null>>
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { isAuth } = useContext(AuthContext)

    const amountToSendRef = useRef<string>('')
    const selectedCountryRef = useRef<string>('')
    const selectedCurrencyRef = useRef<string>('')
    const selectedBankingPartnerRef = useRef<string>('')
    const customerDataRef = useRef<Partial<Customer> | null>(null)

    const [balanceSet, setBalanceSet] = useState<boolean>(false)
    const [amountToSend, setAmountToSend] = useState<string>('')
    const [errors, setErrors] = useState<{ [key: string]: string }>()
    const [selectedCountry, setSelectedCountry] = useState<string>('')
    const [selectedCurrency, setSelectedCurrency] = useState<string>('')
    const [showBankNamesData, setShowBankNamesData] = useState<boolean>(true)
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)
    const [selectedBankingPartner, setSelectedBankingPartner] = useState<string>('')
    const [bankingPartnersToSelect, setBankingPartnersToSelect] = useState<Array<{ name: string }>>([])
    const [availableBankAccountTypes, setAvailableBankAccountTypes] = useState<{ [key: string]: string }>()
    const [currentBalance, setCurrentBalance] = useState<{ balance: number; currency: string } | null>(null)

    useImperativeHandle(
      ref,
      () => {
        return {
          handleSendPayment: () => {
            handleSendPayment()
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    useEffect(() => {
      if (selectedCountry) {
        setErrors({})
        setBalanceSet(false)
        setAmountToSend('')
        setCustomerData(null)
        customerDataRef.current = null
        setCurrentBalance(null)
        setSelectedCurrency('')
        selectedCurrencyRef.current = ''
        handleEnableActionButton(false)
        setSelectedBankingPartner('')
        selectedBankingPartnerRef.current = ''
        setShowBankNamesData(true)
        setAvailableBankAccountTypes(undefined)

        setTimeout(() => {
          selectedCurrencyRef.current = (countriesCurrencies as { [key: string]: string })[selectedCountry]
          setSelectedCurrency((countriesCurrencies as { [key: string]: string })[selectedCountry])
          setBankingPartnersToSelect((bankingPartnersByCountry as { [key: string]: Array<{ name: string }> })[selectedCountry])
        }, 1)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountry])

    useEffect(() => {
      if (selectedBankingPartner) {
        const availableBankAccountTypesToSet = {} as { [key: string]: string }

        if (selectedBankingPartner !== 'COBRE' && selectedBankingPartner !== 'PASSPORT') {
          for (const bankAccountTypeKey of Object.keys(customersBankAccountType)) {
            if (bankAccountTypeKey === '1' || bankAccountTypeKey === '2') {
              availableBankAccountTypesToSet[bankAccountTypeKey] = customersBankAccountType[bankAccountTypeKey]
            }
          }

          setShowBankNamesData(true)
        }

        if (selectedBankingPartner === 'COBRE') {
          for (const bankAccountTypeKey of Object.keys(customersBankAccountType)) {
            if (bankAccountTypeKey === '1' || bankAccountTypeKey === '2' || bankAccountTypeKey === '3') {
              availableBankAccountTypesToSet[bankAccountTypeKey] = customersBankAccountType[bankAccountTypeKey]
            }
          }

          setShowBankNamesData(true)
        }

        if (selectedBankingPartner === 'PASSPORT') {
          for (const bankAccountTypeKey of Object.keys(customersBankAccountType)) {
            if (bankAccountTypeKey === '3') {
              availableBankAccountTypesToSet[bankAccountTypeKey] = customersBankAccountType[bankAccountTypeKey]
            }
          }

          setShowBankNamesData(false)
        }

        setErrors({})
        setBalanceSet(false)
        setAmountToSend('')
        setCustomerData(null)
        customerDataRef.current = null
        handleIsLoading(true)
        handleEnableActionButton(false)
        setAvailableBankAccountTypes(availableBankAccountTypesToSet)
        handleGetBankBalanceBySelectedBakingPartner()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBankingPartner])

    const handleResetProcess = () => {
      amountToSendRef.current = ''
      customerDataRef.current = null
      selectedCountryRef.current = ''
      selectedCurrencyRef.current = ''
      selectedBankingPartnerRef.current = ''

      setAmountToSend('')
      setCustomerData(null)
      setSelectedCountry('')
      setSelectedCurrency('')
      setSelectedBankingPartner('')
    }

    const handleSendPayment = () => {
      handleValidateNewCustomerForm()
    }

    const handleNormalizeMoneyString = (str: string) => {
      let cleaned = str.replace(/[$,\s]/g, '')
      let num = Number(cleaned)

      if (Number.isInteger(num)) {
        return num
      }

      return num
    }

    const handleValidateNewCustomerForm = async () => {
      setErrors({})
      let formDataIsValid = true
      const formErrors: { [key: string]: string } = {}

      if (!customerDataRef.current?.firstName) {
        formDataIsValid = false
        formErrors.firstName = t('newCustomerModal.customerDataForm.errors.emptyFirstName', { ns: 'customers-management' })
      }

      if (!customerDataRef.current?.lastName) {
        formDataIsValid = false
        formErrors.lastName = t('newCustomerModal.customerDataForm.errors.emptyLastName', { ns: 'customers-management' })
      }

      if (!customerDataRef.current?.documentType) {
        formDataIsValid = false
        formErrors.documentType = t('newCustomerModal.customerDataForm.errors.emptyDocumentType', { ns: 'customers-management' })
      }

      if (!customerDataRef.current?.documentNumber) {
        formDataIsValid = false
        formErrors.documentNumber = t('newCustomerModal.customerDataForm.errors.emptyDocumentNumber', { ns: 'customers-management' })
      }

      if (selectedCountryRef.current === 'COL') {
        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankAccountType)
        ) {
          formDataIsValid = false
          formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
        }

        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            (customerDataRef.current?.bankAccountsData[0].bankAccountType === '1' || customerDataRef.current?.bankAccountsData[0].bankAccountType === '2') &&
            !customerDataRef.current?.bankAccountsData[0].bankName)
        ) {
          formDataIsValid = false
          formErrors.bankName = t('newCustomerModal.customerDataForm.errors.emptyBankName', { ns: 'customers-management' })
        }

        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankNumber)
        ) {
          formDataIsValid = false
          formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
        }
      }

      if (selectedCountryRef.current === 'BRA') {
        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankAccountType)
        ) {
          formDataIsValid = false
          formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
        }

        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankName)
        ) {
          formDataIsValid = false
          formErrors.bankName = t('newCustomerModal.customerDataForm.errors.emptyBankName', { ns: 'customers-management' })
        }

        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankNumber)
        ) {
          formDataIsValid = false
          formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
        }
      }

      if (selectedCountryRef.current === 'MEX') {
        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankAccountType)
        ) {
          formDataIsValid = false
          formErrors.bankAccountType = t('newCustomerModal.customerDataForm.errors.emptyBankAccountType', { ns: 'customers-management' })
        }

        if (
          !Array.isArray(customerDataRef.current?.bankAccountsData) ||
          (Array.isArray(customerDataRef.current?.bankAccountsData) &&
            customerDataRef.current?.bankAccountsData.length > 0 &&
            customerDataRef.current?.bankAccountsData[0] &&
            !customerDataRef.current?.bankAccountsData[0].bankNumber)
        ) {
          formDataIsValid = false
          formErrors.bankAccountNumber = t('newCustomerModal.customerDataForm.errors.emptyBankAccountNumber', { ns: 'customers-management' })
        }
      }

      if (!amountToSendRef.current) {
        formDataIsValid = false
        formErrors.amountToSend = t('newCustomerModal.customerDataForm.errors.paymentAmountEmpty', { ns: 'customers-management' })
      }

      if (amountToSendRef.current && Number(amountToSendRef.current.replaceAll('$', '').replaceAll(',', '').trim()) === 0) {
        formDataIsValid = false
        formErrors.amountToSend = t('newCustomerModal.customerDataForm.errors.paymentAmountEmpty', { ns: 'customers-management' })
      }

      if (!formDataIsValid) {
        return setErrors(formErrors)
      }

      try {
        handleIsLoading(true)
        const {
          data: { accessToken }
        } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        const amountToSendNormalized = handleNormalizeMoneyString(amountToSendRef.current)

        await sendPaymentTest(amountToSendNormalized, customerDataRef.current, selectedCountryRef.current, selectedCurrencyRef.current, selectedBankingPartnerRef.current, accessToken)
        setShowSuccessMessage(true)
        handleResetProcess()
        handleIsLoading(false)
        setTimeout(() => {
          setShowSuccessMessage(false)
        }, 10_000)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        handleIsLoading(false)
        return
      }
    }

    const handleGetBankBalanceBySelectedBakingPartner = async () => {
      if (selectedBankingPartner !== 'IRIS') {
        try {
          const {
            data: { accessToken }
          } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
          const {
            data: { currentBalance, currency }
          } = await getBalanceBySelectedBankingPartner(selectedCountry, selectedBankingPartner, accessToken)

          setCurrentBalance({
            currency,
            balance: currentBalance
          })
          handleIsLoading(false)
          handleEnableActionButton(true)
          setBalanceSet(true)
          return
        } catch (error: any) {
          console.log(error, error?.response?.data?.message)
          if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
            handleCloseSession()
          } else if (error?.response?.status === 401) {
            setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
          } else {
            setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
          }

          handleIsLoading(false)
          return
        }
      }

      setCurrentBalance(null)
      handleIsLoading(false)
      setBalanceSet(true)
      handleEnableActionButton(true)
    }

    const formatThousands = (str: string) => {
      const parts = str.toString().split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    }

    const handleGetAmountQuantity = (valueToSet: string) => {
      const extractedNumberOfValueToSet = valueToSet.replaceAll('$', '').replaceAll(',', '').trim()

      if (isNaN(Number(extractedNumberOfValueToSet)) && extractedNumberOfValueToSet.slice(-1) !== '.') return ''

      if (extractedNumberOfValueToSet === '') return ''

      return parseNumber(extractedNumberOfValueToSet)
    }

    const handleSetPaymentAmountToSend = (paymentAmountToSend: string) => {
      const paymentAmountToset = handleGetAmountQuantity(paymentAmountToSend)
      amountToSendRef.current = paymentAmountToset
      setAmountToSend(paymentAmountToset)
    }

    return (
      <div className="customers-add-new-form-container">
        <div className="customers-add-new-form-personal-data-container">
          <div className="customers-add-new-form-personal-data-title">Ingresa los datos necesarios para el envío del pago</div>
          <div style={{ textAlign: 'center', padding: '20px', boxSizing: 'border-box', color: '#EF4444', backgroundColor: '#FEF2F2', borderRadius: '15px', border: '1px solid #EF4444' }}>
            TENER EN CUENTA: Este método que se va a usar aquí tiene repercusiones en producción, es decir, se está tratando con dinero real
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center', width: '100%' }}>
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label="País a donde vas a enviar el pago"
              value={selectedCountry || ''}
              onChange={(event) => {
                selectedCountryRef.current = event.target.value
                setSelectedCountry(event.target.value)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                Selecciona
              </MenuItem>
              {Object.keys(countriesCurrencies).map((currency: string) => {
                return (
                  <MenuItem
                    key={currency}
                    value={currency}
                  >
                    {`${(countriesNames as any)[currency].toUpperCase()} (${currency.toUpperCase()})`}
                  </MenuItem>
                )
              })}
            </TextField>
            {selectedCountry && (
              <TextField
                select
                fullWidth
                variant="standard"
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                inputProps={{
                  style: { textAlign: 'center' }
                }}
                label="Riel a usar para enviar el pago"
                value={selectedBankingPartner || ''}
                onChange={(event) => {
                  setSelectedBankingPartner(event.target.value)
                  selectedBankingPartnerRef.current = event.target.value
                }}
              >
                <MenuItem
                  disabled
                  value=""
                >
                  Selecciona
                </MenuItem>
                {bankingPartnersToSelect?.map((bankingPartnerData) => {
                  return (
                    <MenuItem
                      key={bankingPartnerData.name}
                      value={bankingPartnerData.name}
                    >
                      {bankingPartnerData.name}
                    </MenuItem>
                  )
                })}
              </TextField>
            )}
            {selectedCountry && selectedBankingPartner && balanceSet && currentBalance && (
              <div style={{ width: '100%', display: 'flex' }}>
                <TextField
                  fullWidth
                  disabled
                  variant="standard"
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-input.MuiInput-input.Mui-disabled': { '-webkit-text-fill-color': '#00000095', textAlign: 'right' } }}
                  label="Saldo actual"
                  value={`$ ${formatThousands(String(currentBalance.balance))} ${currentBalance.currency.toUpperCase()}`}
                />
              </div>
            )}
            {selectedCountry && selectedBankingPartner && balanceSet && !currentBalance && (
              <div style={{ width: '100%', color: '#737373', textAlign: 'center', fontSize: '16px', fontWeight: 400, lineHeight: '150%' }}>El riel no expone la información del saldo</div>
            )}
            {selectedCountry && selectedBankingPartner && balanceSet && (
              <>
                {selectedCountry === 'COL' && (
                  <ColombianCustomerPaymentForm
                    errors={errors}
                    customerData={customerData}
                    amountToSend={amountToSend}
                    selectedCurrency={selectedCurrency}
                    showBankNamesData={showBankNamesData}
                    setShowBankNamesData={setShowBankNamesData}
                    customersDocumentType={customersDocumentType}
                    availableBankAccountTypes={availableBankAccountTypes}
                    handleSetPaymentAmountToSend={handleSetPaymentAmountToSend}
                    handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                      customerDataRef.current = {
                        ...customerDataRef.current,
                        [attributeToSet]: valueToSet
                      }
                      setCustomerData((prevCustomerData: Partial<Customer> | null) => ({
                        ...prevCustomerData,
                        [attributeToSet]: valueToSet
                      }))
                    }}
                  />
                )}
                {selectedCountry === 'MEX' && (
                  <MexicanCustomerPaymentForm
                    errors={errors}
                    customerData={customerData}
                    amountToSend={amountToSend}
                    selectedCurrency={selectedCurrency}
                    handleSetPaymentAmountToSend={handleSetPaymentAmountToSend}
                    handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                      customerDataRef.current = {
                        ...customerDataRef.current,
                        [attributeToSet]: valueToSet
                      }
                      setCustomerData((prevCustomerData: Partial<Customer> | null) => ({
                        ...prevCustomerData,
                        [attributeToSet]: valueToSet
                      }))
                    }}
                  />
                )}
                {selectedCountry === 'BRA' && (
                  <BrazilianCustomerPaymentForm
                    errors={errors}
                    customerData={customerData}
                    amountToSend={amountToSend}
                    selectedCurrency={selectedCurrency}
                    handleSetPaymentAmountToSend={handleSetPaymentAmountToSend}
                    handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                      customerDataRef.current = {
                        ...customerDataRef.current,
                        [attributeToSet]: valueToSet
                      }
                      setCustomerData((prevCustomerData: Partial<Customer> | null) => ({
                        ...prevCustomerData,
                        [attributeToSet]: valueToSet
                      }))
                    }}
                  />
                )}
                {selectedCountry === 'ARG' && (
                  <AgentinianCustomerPaymentForm
                    errors={errors}
                    customerData={customerData}
                    amountToSend={amountToSend}
                    selectedCurrency={selectedCurrency}
                    handleSetPaymentAmountToSend={handleSetPaymentAmountToSend}
                    handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                      customerDataRef.current = {
                        ...customerDataRef.current,
                        [attributeToSet]: valueToSet
                      }
                      setCustomerData((prevCustomerData: Partial<Customer> | null) => ({
                        ...prevCustomerData,
                        [attributeToSet]: valueToSet
                      }))
                    }}
                  />
                )}
                {selectedCountry === 'CHI' && (
                  <ChileanCustomerPaymentForm
                    errors={errors}
                    customerData={customerData}
                    amountToSend={amountToSend}
                    selectedCurrency={selectedCurrency}
                    handleSetPaymentAmountToSend={handleSetPaymentAmountToSend}
                    handleSetCustomerData={(attributeToSet: string, valueToSet: string | Array<BankAccount>) => {
                      customerDataRef.current = {
                        ...customerDataRef.current,
                        [attributeToSet]: valueToSet
                      }
                      setCustomerData((prevCustomerData: Partial<Customer> | null) => ({
                        ...prevCustomerData,
                        [attributeToSet]: valueToSet
                      }))
                    }}
                  />
                )}
              </>
            )}
          </div>
          {errors?.generalError && <div className="error-message">{errors.generalError}</div>}
          {showSuccessMessage && (
            <div style={{ textAlign: 'center', width: '100%', padding: '20px', boxSizing: 'border-box', color: '#15803D', backgroundColor: '#F0FDF4', borderRadius: '15px', border: '1px solid #15803D' }}>
              El pago fue con éxito y serás notificado por correo electrónico las métricas del ciclo del ago
            </div>
          )}
        </div>
      </div>
    )
  }
)
